import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { isAuthenticated, isJobSaved, toggleSavedJob } from '../utils/userData';
import './JobDetails.css';

const JobDetails = () => {
  const navigate = useNavigate();
  const locationState = useLocation();
  const [job, setJob] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [isSaved, setIsSaved] = React.useState(false);

  React.useEffect(() => {
    // Get job data from location state passed from Jobs page
    if (locationState?.state?.job) {
      setJob(locationState.state.job);
      setLoading(false);
    } else {
      // Job not found - redirect to jobs page
      setTimeout(() => {
        navigate('/jobs');
      }, 2000);
    }
  }, [locationState, navigate]);

  React.useEffect(() => {
    if (job?.id) {
      setIsSaved(isJobSaved(job.id));
    }
  }, [job]);

  const handleSaveJob = () => {
    if (!job) {
      return;
    }

    if (!isAuthenticated()) {
      toast.info('Please login or sign up to save this job.');
      navigate('/job-login');
      return;
    }

    const added = toggleSavedJob(job);
    setIsSaved(added);

    if (added) {
      toast.success('Job saved successfully.');
    } else {
      toast.info('Job removed from saved list.');
    }
  };

  if (loading) {
    return (
      <div className="job-details-page">
        <Navbar />
        <div className="loading-state">
          <div className="loader"></div>
          <p>Loading job details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-details-page">
        <Navbar />
        <div className="empty-state">
          <h3>Job not found</h3>
          <p>The job you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/jobs')} className="btn-back">
            ← Back to Jobs
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const formatSalary = (min, max) => {
    if (!min || !max) return 'Competitive package';
    return `₹${(min / 100000).toFixed(0)}L - ₹${(max / 100000).toFixed(0)}L`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not available';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Handle backend response with snake_case field names
  const companyName = job.company?.display_name || job.company?.displayName || job.company?.name || job.company || 'Company Name';
  const location =
    job.location?.display_name ||
    job.location?.displayName ||
    job.location?.name ||
    job.location ||
    'Location';
  const jobDescription = job.description || 'No description available';
  const postedDate = job.created || null;
  const displayJobId = job.id || 'N/A';
  const contractType = job.contract_time || job.contract_type || 'Not specified';
  const category = job.category?.label || job.category?.tag || 'General';

  const textToParagraphs = (text) => {
    const normalizeLabel = (label) => {
      const key = label.toLowerCase().replace(/\s+/g, ' ').trim();

      const aliases = {
        role: 'Position',
        position: 'Position',
        'job location': 'Job Location',
        location: 'Job Location',
        exp: 'Experience',
        experience: 'Experience',
        responsibilities: 'Responsibilities',
        'role & responsibilities': 'Responsibilities',
        'role and responsibilities': 'Responsibilities',
        'required skills': 'Required Skills',
        'required skill sets': 'Required Skills',
        'skills required': 'Required Skills',
        'must have': 'Must Have',
        'good to have': 'Good To Have',
        'nice to have': 'Good To Have',
        'notice period': 'Notice Period',
        'work mode': 'Work Mode',
        'mode of work': 'Work Mode',
        qualification: 'Qualification',
        qualifications: 'Qualification'
      };

      return aliases[key] || label.trim();
    };

    const hideLabelSet = new Set(['must have', 'good to have', 'required skills']);
    const anchorLabels = [
      'Position',
      'Role',
      'Job Location',
      'Location',
      'Experience',
      'Exp',
      'Role & responsibilities',
      'Role and responsibilities',
      'Responsibilities',
      'Required skill sets',
      'Required skills',
      'Skills required',
      'Must have',
      'Good to have',
      'Nice to have',
      'Notice period',
      'Work mode',
      'Mode of work',
      'Qualification',
      'Qualifications'
    ];

    let normalized = String(text)
      .replace(/\r/g, ' ')
      .replace(/[\t]+/g, ' ')
      .replace(/\s*[|;]+\s*/g, '\n')
      .replace(/\s*[:]\s*/g, ': ')
      .replace(/\s*[-]\s*/g, '- ')
      .replace(/\s+/g, ' ')
      .trim();

    // Force breaks for common JD labels so single-line API text becomes readable.
    anchorLabels.forEach((label) => {
      const token = label.replace(/\s+/g, '\\s+');
      const regex = new RegExp(`\\s+(?=${token}\\s*[:-])`, 'gi');
      normalized = normalized.replace(regex, '\n');
    });

    normalized = normalized
      .replace(/\.\s+(?=[A-Z][A-Za-z\s&/]{2,30}\s*[:-])/g, '.\n')
      .replace(/\n+/g, '\n');

    const lines = normalized
      .split('\n')
      .map((line) => line.trim().replace(/^[-•]+\s*/, ''))
      .filter(Boolean);

    const results = [];
    const seen = new Set();

    lines.forEach((line) => {
      const keyValue = line.match(/^([A-Za-z][A-Za-z\s&/]{1,40})\s*[:-]\s*(.+)$/);

      if (!keyValue) {
        const plain = line.replace(/\s+/g, ' ').trim();
        const plainKey = plain.toLowerCase();
        if (plain && !seen.has(plainKey)) {
          seen.add(plainKey);
          results.push(plain);
        }
        return;
      }

      const rawLabel = keyValue[1].replace(/\s+/g, ' ').trim();
      const value = keyValue[2].replace(/\s+/g, ' ').trim();

      if (!value) {
        return;
      }

      const displayLabel = normalizeLabel(rawLabel);
      const labelKey = displayLabel.toLowerCase();

      const displayLine = hideLabelSet.has(labelKey)
        ? value
        : `${displayLabel}: ${value}`;

      const dedupeKey = displayLine.toLowerCase();
      if (!seen.has(dedupeKey)) {
        seen.add(dedupeKey);
        results.push(displayLine);
      }
    });

    return results;
  };

  return (
    <div className="job-details-page">
      <Navbar />
      
      <div className="job-details-container">
        <button onClick={() => navigate('/jobs')} className="btn-back">
          ← Back to Jobs
        </button>

        <div className="job-details-hero">
          <div className="job-details-title-wrap">
            <p className="job-kicker">Premium Opportunity</p>
            <h1 className="job-details-title">{job.title || 'Job Title'}</h1>
            <p className="job-details-company">
              <span className="company-icon">⚡</span> {companyName}
            </p>
            <p className="job-details-location">
              <span className="location-icon">📍</span> {location}
            </p>

            <div className="job-tags">
              <span>{category}</span>
              <span>{contractType}</span>
            </div>
          </div>

          <div className="job-primary-actions">
            {job.redirect_url ? (
              <a
                href={job.redirect_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-apply-large"
              >
                Apply Now
              </a>
            ) : (
              <button className="btn-apply-large disabled" disabled>
                Apply Link Not Available
              </button>
            )}

            <button className={`btn-save-large ${isSaved ? 'saved' : ''}`} onClick={handleSaveJob}>
              {isSaved ? '♥ Saved' : '♡ Save Job'}
            </button>
          </div>
        </div>

        <div className="job-details-meta-grid">
          <article className="meta-card">
            <h4>Compensation</h4>
            <p>{formatSalary(job.salary_min, job.salary_max)}</p>
          </article>
          <article className="meta-card">
            <h4>Published</h4>
            <p>{formatDate(postedDate)}</p>
          </article>
          <article className="meta-card">
            <h4>Job ID</h4>
            <p className="job-id">{displayJobId}</p>
          </article>
          <article className="meta-card">
            <h4>Source</h4>
            <p>{job.source || 'Platform Feed'}</p>
          </article>
        </div>

        <div className="job-details-content-grid">
          <section className="job-description-section section-card">
            <h2>Role Overview</h2>
            <div className="job-description-text">
              {textToParagraphs(jobDescription).map((line, index) => (
                <p key={`desc-${index}`}>{line}</p>
              ))}
            </div>
          </section>

          {job.requirements && (
            <section className="job-requirements-section section-card">
              <h2>Requirements</h2>
              <div className="requirements-text">
                {textToParagraphs(job.requirements).map((line, index) => (
                  <p key={`req-${index}`}>{line}</p>
                ))}
              </div>
            </section>
          )}

          {job.benefits && (
            <section className="job-benefits-section section-card">
              <h2>Benefits</h2>
              <div className="benefits-text">
                {textToParagraphs(job.benefits).map((line, index) => (
                  <p key={`ben-${index}`}>{line}</p>
                ))}
              </div>
            </section>
          )}

          <section className="job-side-card section-card">
            <h2>Quick Snapshot</h2>
            <ul>
              <li>Company: {companyName}</li>
              <li>Location: {location}</li>
              <li>Category: {category}</li>
              <li>Contract: {contractType}</li>
            </ul>
            <p className="snapshot-note">
              Save this role now and track it anytime from your Saved Jobs section.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JobDetails;
